import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import Case from 'case';
import { Box } from 'grommet';
import { Seo } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  refreshReportAccessDataRequest,
  clearReportAccessDataRequest,
} from '@Actions';
import { secondsSinceEpoch, baseServerUrl } from '@Helpers/util';
import { DomoEmbedPartial } from '@Components/Partial/Product/Overview';
import { CopyLinkButton } from './PiracyOverviewPage';


const ReportPage = ({
  small,
  loading,
  location,
  customReportsConfig = null,
  categoryReports = null,
  refreshAccessData,
  clearAccessData,
  companyId,
  companyName,
  accessToken = null,
  accessTokenExpiration = null,
  paymentPending,
}) => {
  const [embedToken, setEmbedToken] = React.useState(null);
  const [embedData, setEmbedData] = React.useState({
    embedId: null,
    embedTitle: null,
    applyCompanyFilter: null,
    pageTitle: null,
  });
  const { id } = useParams();
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  const customReports = customReportsConfig && customReportsConfig.find(({ metadata }) => (
    metadata?.config?.companyId === companyId && metadata?.config?.companyName === companyName));

  React.useEffect(() => {
    const customEmbed = customReports?.metadata?.config?.customReports?.find(
      ({ embedUid }) => embedUid === id,
    );

    if (customEmbed) {
      const activeEmbedId = (isDarkMode && customEmbed.darkModeEmbedId)
        ? customEmbed.darkModeEmbedId
        : customEmbed.embedId;
      setEmbedData({
        embedId: activeEmbedId,
        embedTitle: customEmbed.embedTitle,
        applyCompanyFilter: customEmbed.applyCompanyFilter,
        pageTitle: `Custom Report - ${customEmbed.embedTitle}`,
      });
    } else if (categoryReports) {
      const formattedReports = Object.entries(categoryReports).map(([key, value]) => {
        const values = Object.entries(value).map(([k, v]) => {
          const name = v.customReportName || Case.title(k);
          return { ...v, embedTitle: name, category: key };
        });

        return { [key]: values };
      });

      const allReports = formattedReports?.length > 0
        && formattedReports.map((category) => (Object.values(category)[0])).flat();

      const currentEmbed = allReports?.find(({ embedUid }) => embedUid === id);
      const currentCategory = allReports?.find(({ embedUid }) => embedUid === id)?.category;
      const displayCategory = currentCategory && Case.title(currentCategory.replace('Reports', ''));
      const activeEmbedId = (isDarkMode && currentEmbed.darkModeEmbedId)
        ? currentEmbed.darkModeEmbedId
        : currentEmbed.embedId;

      setEmbedData({
        embedId: activeEmbedId,
        embedTitle: currentEmbed.embedTitle,
        applyCompanyFilter: currentEmbed.applyCompanyFilter,
        pageTitle: `${displayCategory} - ${currentEmbed.embedTitle}`,
      });
    }
  }, [customReports, categoryReports, isDarkMode]);

  const fetchServerEmbedToken = async (useToken) => {
    const options = { method: 'GET' };
    let embedParams = `token=${useToken}&embedId=${embedData.embedId}`;

    if (embedData.applyCompanyFilter) {
      embedParams += `&companyId=${companyId}`;
    }

    const response = await fetch(`${baseServerUrl()}api/embedToken?${embedParams}`, options);
    const responseContent = await response.text();
    setEmbedToken(responseContent);
  };

  React.useEffect(() => {
    if (!accessToken || accessTokenExpiration - 60 > secondsSinceEpoch()) {
      refreshAccessData();
    }

    return () => {
      clearAccessData();
    };
  }, []);

  React.useEffect(() => {
    if (accessToken && embedData.embedId) {
      setEmbedToken(null);
      fetchServerEmbedToken(accessToken);
    }
  }, [accessToken, embedData.embedId]);

  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      smallBanner
      hideFooter
      small={small}
      location={location}
      bannerProps={{
        title: embedData.pageTitle || 'Loading report...',
        subTitle: 'Analytics',
        bannerChildren: !paymentPending && (
          <Box
            direction="row"
            justify="center"
            align="center"
            gap="1rem"
          >
            <CopyLinkButton
              small={small}
              textColor={isDarkMode ? button.primaryText : page.primaryText}
              highlightColor={button.hoverText}
              buttonBg={isDarkMode ? button.hoverBg : page.altBg}
              location={location}
              pageTitle={embedData.pageTitle}
              embedId={embedData.embedId}
              applyFilter={embedData.applyCompanyFilter}
              companyId={companyId}
              renderKey="report"
            />
          </Box>
        ),
        bannerChildrenPosition: 'right',
      }}
    >
      <Seo />
      <Box flex background={usePageBg} pad={{ vertical: '1rem', horizontal: small ? '0.5rem' : '4vw' }} height="100%">
        <DomoEmbedPartial
          loading={loading}
          textColor={page.primaryText}
          highlightColor={page.highlight}
          embedId={embedData.embedId}
          embedToken={embedToken}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    accessToken: state.productEmbed.reportAccessToken,
    accessTokenExpiration: state.productEmbed.reportAccessTokenExpiration,
    loading: state.fetchLoader.dataLoading,
    customReportsConfig: state.appConfig.customReportsConfig,
    companyId: state.auth.companyId,
    companyName: state.auth.companyName,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    refreshAccessData: refreshReportAccessDataRequest,
    clearAccessData: clearReportAccessDataRequest,
  }, dispatch);
}

ReportPage.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  customReportsConfig: PropTypes.arrayOf(PropTypes.any),
  categoryReports: PropTypes.objectOf(PropTypes.objectOf(PropTypes.shape({
    embedId: PropTypes.string.isRequired,
    embedUid: PropTypes.string.isRequired,
    customReportName: PropTypes.string,
    applyCompanyFilter: PropTypes.bool,
  }))),
  refreshAccessData: PropTypes.func.isRequired,
  clearAccessData: PropTypes.func.isRequired,
  accessToken: PropTypes.string,
  accessTokenExpiration: PropTypes.number,
  companyId: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired,
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ReportPage));
